import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, styled } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import * as pdfjsLib from 'pdfjs-dist';

const PDF_WORKER_URL = 'https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js';
pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_URL;

const Viewer = styled(Box, { shouldForwardProp: (p) => p !== 'scrollable' })<{ scrollable?: boolean }>`
  position: relative;
  width: 100%;
  height: 720px;
  background-color: #f2f2f2;
  border-radius: 20px;
  overflow: hidden;

  .pdf-pages {
    height: 100%;
    ${({ scrollable }) =>
      scrollable
        ? `
      overflow-y: auto;
      padding: 0;
      box-sizing: border-box;
      scrollbar-width: thin;
      scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
      &::-webkit-scrollbar { width: 6px; }
      &::-webkit-scrollbar-track { background: transparent; }
      &::-webkit-scrollbar-thumb { background: rgba(0, 0, 0, 0.2); border-radius: 3px; }
    `
        : `
      overflow: hidden;
      transition: opacity 0.2s ease;
    `}
  }

  .overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
  }

  ${({ scrollable }) =>
    !scrollable &&
    `
    &:hover .pdf-pages { opacity: 0.06; }
    &:hover .overlay { opacity: 1; pointer-events: auto; }
  `}
`;

const OverlayLabel = styled('p')`
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 24px;
  color: #000;
  text-align: center;
  max-width: 415px;
`;

const ReadButton = styled(Button)({
  height: 50,
  minWidth: 0,
  backgroundColor: "#d1efff",
  color: "#000",
  border: "none",
  borderRadius: 20,
  fontFamily: "'IBM Plex Mono', monospace",
  fontWeight: 700,
  fontSize: 14,
  lineHeight: "19.2px",
  letterSpacing: 0,
  textTransform: "uppercase",
  padding: "9.5px 42px 10.5px",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#b8e3fa",
    boxShadow: "none",
    border: "none",
  },
});

interface PdfPreviewProps {
  fileUrl: string;
  overlayLabel?: string;
  /** When true, the PDF is directly scrollable (no hover overlay). Use in embedded flip gallery. */
  scrollable?: boolean;
}

const PdfPreview: React.FC<PdfPreviewProps> = ({ fileUrl, overlayLabel, scrollable }) => {
  const pagesRef = useRef<HTMLDivElement>(null);
  const [, setRenderedFor] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    const container = pagesRef.current;
    if (!container || !fileUrl) return;

    container.innerHTML = '';
    setRenderedFor('');

    (async () => {
      try {
        const pdf = await pdfjsLib.getDocument(fileUrl).promise;
        if (cancelled) return;

        const containerWidth = container.clientWidth || 600;
        const dpr = window.devicePixelRatio || 1;

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          if (cancelled) return;

          const baseViewport = page.getViewport({ scale: 1 });
          const cssScale = containerWidth / baseViewport.width;
          const viewport = page.getViewport({ scale: cssScale * dpr });

          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          canvas.style.display = 'block';
          canvas.style.borderRadius = '8px';
          canvas.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.12)';
          canvas.style.marginBottom = i < pdf.numPages ? '16px' : '0';
          container.appendChild(canvas);

          const ctx = canvas.getContext('2d');
          if (!ctx) continue;
          await page.render({ canvasContext: ctx, viewport }).promise;
          if (cancelled) return;
        }

        setRenderedFor(fileUrl);
      } catch (err) {
        console.error('Failed to render PDF', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  const handleRead = () => {
    if (fileUrl) window.open(fileUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Viewer scrollable={scrollable}>
      <div className="pdf-pages" ref={pagesRef} />
      {!scrollable && (
        <div className="overlay">
          <OverlayLabel>
            {overlayLabel || 'Read document before signing with wallet.'}
          </OverlayLabel>
          <InfoOutlinedIcon sx={{ fontSize: 53, color: '#000' }} />
          <ReadButton onClick={handleRead} variant="contained" disableElevation>
            Read
          </ReadButton>
        </div>
      )}
    </Viewer>
  );
};

export default PdfPreview;
